export type SettingType = 'boolean' | 'number' | 'string' | 'select' | 'textarea' | 'color';

export interface SettingMetadata<T = unknown> {
  label: string;
  description?: string;
  type: SettingType;
  defaultValue: T;
  // For select/enum types
  options?: Array<{ value: T; label: string }>;
  // For number types
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  // UI hints
  section?: string;
  order?: number;
  // Implementation status
  disabled?: boolean;
  disabledReason?: string;
}

// Phase 1 Settings Only
export interface Settings {
  ui: {
    theme: 'light' | 'dark' | 'auto';
    fontSize: 'small' | 'medium' | 'large';
    compactMode: boolean;
    iconSize: 'small' | 'medium' | 'large';
  };
  panels: {
    keepWidthOnRefresh: boolean;
    defaultWidth: number;
    animationDuration: number;
  };
  annie: {
    enabled: boolean;
    autoSuggestNoteContent: boolean;
    offerSuggestionsOnBlankPage: boolean;
    annieContext: string;
    organizeContext: string;
    tools: {
      findRelatedDocuments: boolean;
      makeSuggestions: boolean;
      enableWebResearch: boolean;
    };
  };
  highlights: {
    defaultColor: string;
    noteHighlightColor: string;
  };
  editor: {
    autoSave: boolean;
    autoSaveDelay: number;
    spellCheck: boolean;
  };
}

// Type for dot-notation paths
export type SettingPath =
  | `ui.${keyof Settings['ui']}`
  | `panels.${keyof Settings['panels']}`
  | `annie.${keyof Settings['annie']}`
  | 'annie.tools.findRelatedDocuments'
  | 'annie.tools.makeSuggestions'
  | 'annie.tools.enableWebResearch'
  | `highlights.${keyof Settings['highlights']}`
  | `editor.${keyof Settings['editor']}`;

// Metadata for each setting (drives UI generation + defaults)
export const SETTINGS_CONFIG: {
  [K in keyof Settings]: K extends 'annie'
    ? {
        [P in keyof Settings[K]]: P extends 'tools'
          ? {
              [T in keyof Settings[K][P]]: SettingMetadata<Settings[K][P][T]>;
            }
          : SettingMetadata<Settings[K][P]>;
      }
    : {
        [P in keyof Settings[K]]: SettingMetadata<Settings[K][P]>;
      };
} = {
  ui: {
    theme: {
      label: 'Theme',
      description: 'Color scheme for the app',
      type: 'select',
      defaultValue: 'auto',
      options: [
        { value: 'light', label: 'Light' },
        { value: 'dark', label: 'Dark' },
        { value: 'auto', label: 'Auto (system)' },
      ],
      section: 'Appearance',
      order: 1,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    fontSize: {
      label: 'Font size',
      description: 'Base font size throughout the app',
      type: 'select',
      defaultValue: 'medium',
      options: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large' },
      ],
      section: 'Appearance',
      order: 2,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    compactMode: {
      label: 'Compact mode',
      description: 'Reduce spacing for more content',
      type: 'boolean',
      defaultValue: false,
      section: 'Appearance',
      order: 3,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    iconSize: {
      label: 'Icon size',
      description: 'Size of icons throughout the app',
      type: 'select',
      defaultValue: 'medium',
      options: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large' },
      ],
      section: 'Appearance',
      order: 4,
    },
  },
  panels: {
    keepWidthOnRefresh: {
      label: 'Remember panel sizes',
      description: 'Keep panel widths when you reload the app',
      type: 'boolean',
      defaultValue: false,
      section: 'Panel Behavior',
      order: 1,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    defaultWidth: {
      label: 'Default panel width',
      description: 'Width in pixels for new panels',
      type: 'number',
      defaultValue: 400,
      min: 200,
      max: 1000,
      step: 50,
      unit: 'px',
      section: 'Panel Behavior',
      order: 2,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    animationDuration: {
      label: 'Animation duration',
      description: 'How long panel transitions take',
      type: 'number',
      defaultValue: 200,
      min: 0,
      max: 1000,
      step: 50,
      unit: 'ms',
      section: 'Panel Behavior',
      order: 3,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
  },
  annie: {
    enabled: {
      label: 'Enable Annie',
      description: 'Master toggle for AI assistant features',
      type: 'boolean',
      defaultValue: true,
      section: 'AI Assistant',
      order: 1,
    },
    autoSuggestNoteContent: {
      label: 'Auto-suggest note content',
      description: 'Show suggestions when creating empty notes',
      type: 'boolean',
      defaultValue: true,
      section: 'AI Assistant',
      order: 2,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    offerSuggestionsOnBlankPage: {
      label: 'Offer suggestions on blank page',
      description: 'Proactively suggest content when starting fresh',
      type: 'boolean',
      defaultValue: true,
      section: 'AI Assistant',
      order: 3,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    annieContext: {
      label: 'General context for Annie',
      description: 'Background context sent with every Annie conversation (e.g., your research area, preferences)',
      type: 'textarea',
      defaultValue: '',
      section: 'AI Assistant',
      order: 4,
    },
    organizeContext: {
      label: 'Organization preferences',
      description:
        'Instructions for how Annie should organize your notes (e.g., "prefer linking over hubs", "group by topic")',
      type: 'textarea',
      defaultValue: '',
      section: 'AI Assistant',
      order: 5,
    },
    tools: {
      findRelatedDocuments: {
        label: 'Find related documents',
        description: 'Allow Annie to search for related web documents',
        type: 'boolean',
        defaultValue: true,
        section: 'AI Tools',
        order: 1,
      },
      makeSuggestions: {
        label: 'Make suggestions',
        description: 'Allow Annie to suggest improvements to notes',
        type: 'boolean',
        defaultValue: true,
        section: 'AI Tools',
        order: 2,
      },
      enableWebResearch: {
        label: 'Enable web research',
        description: 'Allow Annie to search the web for articles and information using Perplexity',
        type: 'boolean',
        defaultValue: true,
        section: 'AI Tools',
        order: 3,
      },
    },
  },
  highlights: {
    defaultColor: {
      label: 'Default highlight color',
      description: 'Primary color for new highlights',
      type: 'color',
      defaultValue: '#fff192',
      section: 'Highlights',
      order: 1,
    },
    noteHighlightColor: {
      label: 'Note highlight color',
      description: 'Color for highlights with attached notes',
      type: 'color',
      defaultValue: '#fff9c9',
      section: 'Highlights',
      order: 2,
    },
  },
  editor: {
    autoSave: {
      label: 'Auto-save',
      description: 'Automatically save changes',
      type: 'boolean',
      defaultValue: true,
      section: 'Editing',
      order: 1,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    autoSaveDelay: {
      label: 'Auto-save delay',
      description: 'Time to wait before auto-saving',
      type: 'number',
      defaultValue: 2000,
      min: 500,
      max: 10000,
      step: 500,
      unit: 'ms',
      section: 'Editing',
      order: 2,
      disabled: true,
      disabledReason: 'Not yet implemented',
    },
    spellCheck: {
      label: 'Spell check',
      description: 'Enable spell checking in editor',
      type: 'boolean',
      defaultValue: true,
      section: 'Editing',
      order: 3,
    },
  },
};

// Generate default settings from metadata
const generateDefaults = (): Settings => {
  return {
    ui: {
      theme: SETTINGS_CONFIG.ui.theme.defaultValue,
      fontSize: SETTINGS_CONFIG.ui.fontSize.defaultValue,
      compactMode: SETTINGS_CONFIG.ui.compactMode.defaultValue,
      iconSize: SETTINGS_CONFIG.ui.iconSize.defaultValue,
    },
    panels: {
      keepWidthOnRefresh: SETTINGS_CONFIG.panels.keepWidthOnRefresh.defaultValue,
      defaultWidth: SETTINGS_CONFIG.panels.defaultWidth.defaultValue,
      animationDuration: SETTINGS_CONFIG.panels.animationDuration.defaultValue,
    },
    annie: {
      enabled: SETTINGS_CONFIG.annie.enabled.defaultValue,
      autoSuggestNoteContent: SETTINGS_CONFIG.annie.autoSuggestNoteContent.defaultValue,
      offerSuggestionsOnBlankPage: SETTINGS_CONFIG.annie.offerSuggestionsOnBlankPage.defaultValue,
      annieContext: SETTINGS_CONFIG.annie.annieContext.defaultValue,
      organizeContext: SETTINGS_CONFIG.annie.organizeContext.defaultValue,
      tools: {
        findRelatedDocuments: SETTINGS_CONFIG.annie.tools.findRelatedDocuments.defaultValue,
        makeSuggestions: SETTINGS_CONFIG.annie.tools.makeSuggestions.defaultValue,
        enableWebResearch: SETTINGS_CONFIG.annie.tools.enableWebResearch.defaultValue,
      },
    },
    highlights: {
      defaultColor: SETTINGS_CONFIG.highlights.defaultColor.defaultValue,
      noteHighlightColor: SETTINGS_CONFIG.highlights.noteHighlightColor.defaultValue,
    },
    editor: {
      autoSave: SETTINGS_CONFIG.editor.autoSave.defaultValue,
      autoSaveDelay: SETTINGS_CONFIG.editor.autoSaveDelay.defaultValue,
      spellCheck: SETTINGS_CONFIG.editor.spellCheck.defaultValue,
    },
  };
};

export const DEFAULT_SETTINGS: Settings = generateDefaults();

// Helper to get metadata for a setting path
export const getSettingMetadata = (path: SettingPath): SettingMetadata | undefined => {
  const parts = path.split('.');
  const [section, field, nestedField] = parts as [keyof Settings, string, string?];

  const sectionConfig = SETTINGS_CONFIG[section];
  if (!sectionConfig) return undefined;

  const fieldConfig = sectionConfig[field as keyof typeof sectionConfig];
  if (!fieldConfig) return undefined;

  // Handle nested paths like annie.tools.findRelatedDocuments
  if (nestedField && typeof fieldConfig === 'object' && !(fieldConfig as SettingMetadata).type) {
    return (fieldConfig as Record<string, SettingMetadata>)[nestedField];
  }

  return fieldConfig as SettingMetadata;
};
